/**
 * P4 L0 阶段卡（客户详情页头部）
 *
 * 显示：
 *   · 倒计时（l0_grace_hours - 已过去小时）
 *   · 加微信尝试次数
 *   · [尝试加微信] [已加上微信]
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn, Pill } from './ui';
import { AddedWechatModal } from './AddedWechatModal';
import { LogAttemptModal } from './LogAttemptModal';

export function L0StageCard({
  customer, onChanged,
}: {
  customer: any;
  onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const [showAdded, setShowAdded] = useState(false);
  const [showAttempt, setShowAttempt] = useState(false);

  const { data: attempts } = useQuery<any[]>({
    queryKey: ['wechat-attempts', customer.id],
    queryFn: () => $.get(`/customers/${customer.id}/wechat-attempts`),
  });
  const { data: cfgRows } = useQuery<any[]>({
    queryKey: ['lifecycle-config'],
    queryFn: () => $.get('/lifecycle-config'),
    staleTime: 60_000,
  });
  const cfg = new Map((cfgRows ?? []).map((r: any) => [r.configKey, r.configValue]));
  const graceHours = Number(cfg.get('l0_grace_hours') ?? 12);
  const attemptThreshold = Number(cfg.get('l0_attempt_threshold') ?? 3);

  const assignedAt = customer.assignedAt ? new Date(customer.assignedAt) : null;
  const hoursSince = assignedAt ? (Date.now() - assignedAt.getTime()) / 3_600_000 : 0;
  const remaining = Math.max(0, graceHours - hoursSince);
  const tone: any = remaining > 4 ? 'success' : remaining > 0 ? 'warning' : 'danger';
  const attemptCount = (attempts ?? []).length;
  const canRequestVoid = attemptCount >= attemptThreshold;

  return (
    <div style={{
      padding: 16, marginBottom: 16,
      background: 'linear-gradient(90deg, var(--bg-1), var(--bg-2))',
      border: '1px solid var(--border)', borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Pill tone="warning">L0 销售员加客户微信</Pill>
        <Pill tone={tone}>
          剩余 {remaining.toFixed(1)} 小时（宽限期 {graceHours} 小时）
        </Pill>
        <Pill tone="default">销售员已尝试 {attemptCount} 次</Pill>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
        销售员尚未加上客户微信。加微信成功后点 <strong>"已加上微信"</strong> 上传通过截图 + 客户微信昵称，自动生成客户内部编号并进入 L1 触达。
        {canRequestVoid && <span style={{ marginLeft: 8, color: 'var(--warning-text)' }}>· 已满足申请作废条件</span>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn kind="default" onClick={() => setShowAttempt(true)}>📝 记录一次尝试</Btn>
        <Btn kind="primary" onClick={() => setShowAdded(true)}>✅ 已加上客户微信</Btn>
        {canRequestVoid && (
          <Btn kind="default" onClick={() => onChanged?.()}>申请作废客户 →</Btn>
        )}
      </div>

      <AddedWechatModal
        open={showAdded}
        customer={customer}
        onClose={() => setShowAdded(false)}
        onDone={() => {
          setShowAdded(false);
          qc.invalidateQueries({ queryKey: ['customer', String(customer.id)] });
          qc.invalidateQueries({ queryKey: ['wechat-attempts', customer.id] });
          onChanged?.();
        }}
      />
      <LogAttemptModal
        open={showAttempt}
        customer={customer}
        onClose={() => setShowAttempt(false)}
        onDone={() => {
          setShowAttempt(false);
          qc.invalidateQueries({ queryKey: ['wechat-attempts', customer.id] });
        }}
      />
    </div>
  );
}
